/**
 * RateGrid — Expandable rate table for a pricing product.
 * Shows adjustments table + rate rows centered around par, with expand/collapse.
 *
 * Props:
 *   product        — product object with prices, adjustments, totalAdjustment, paymentType, term, productShortName
 *   loanAmount     — effective loan amount for payment calcs
 *   selectedRate   — currently selected rate object (for highlighting)
 *   onSelectRate   — callback(rateObj) when a rate row is clicked
 *   showAllRates   — boolean, show all rates or just around par
 *   onToggleShowAll — callback() to toggle showAllRates
 *   ratesAroundPar — number of rates to show above/below par (default 5)
 */
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { fmt, fmtDollar, fmtPct, calcMonthlyPI, calcMonthlyIO, PAR_PRICE_TARGET } from './pricingUtils';
import { NearParBadge } from './NearParIndicators';

const RATES_AROUND_PAR = 5;

const RateGrid = ({
  product: p,
  loanAmount,
  selectedRate,
  onSelectRate,
  showAllRates = false,
  onToggleShowAll,
  ratesAroundPar = RATES_AROUND_PAR,
}) => {
  const sorted = [...p.prices].sort((a, b) => b.adjustedPrice - a.adjustedPrice);

  // Find par index
  let parIdx = 0;
  let minDiff = Math.abs(sorted[0]?.adjustedPrice - PAR_PRICE_TARGET);
  sorted.forEach((rp, i) => {
    const diff = Math.abs(rp.adjustedPrice - PAR_PRICE_TARGET);
    if (diff < minDiff) { minDiff = diff; parIdx = i; }
  });

  const startIdx = Math.max(0, parIdx - ratesAroundPar);
  const endIdx = Math.min(sorted.length, parIdx + ratesAroundPar + 1);
  const visibleRates = showAllRates ? sorted : sorted.slice(startIdx, endIdx);
  const hiddenCount = sorted.length - (endIdx - startIdx);
  const isIO = p.paymentType === 'Interest Only';

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Adjustments table */}
      {p.adjustments.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" fontWeight={700} sx={{ mb: 0.5, display: 'block' }}>Price Adjustments</Typography>
          <TableContainer>
            <Table size="small" sx={{ '& td': { fontSize: '0.75rem', py: 0.25 } }}>
              <TableBody>
                {p.adjustments.map((adj, i) => (
                  <TableRow key={i}>
                    <TableCell>{adj.description}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: adj.points > 0 ? 'error.main' : adj.points < 0 ? 'success.main' : 'text.primary' }}>
                      {adj.points > 0 ? '-' : adj.points < 0 ? '+' : ''}{fmt(Math.abs(adj.points), 3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: p.totalAdjustment > 0 ? 'error.main' : p.totalAdjustment < 0 ? 'success.main' : 'text.primary' }}>
                    {p.totalAdjustment > 0 ? '-' : p.totalAdjustment < 0 ? '+' : ''}{fmt(Math.abs(p.totalAdjustment), 3)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Rate rows */}
      <Box sx={{ maxHeight: 450, overflowY: 'auto', overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { fontSize: '0.8rem', py: 0.5 } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell sx={{ fontWeight: 700 }}>Rate</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Base Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Adj. Price</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{isIO ? 'Est. IO' : 'Est. P&I'}</TableCell>
              <TableCell sx={{ width: 52 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!showAllRates && startIdx > 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 0.5 }}>
                  <Button size="small" onClick={onToggleShowAll} sx={{ fontSize: '0.7rem', textTransform: 'none' }} startIcon={<ExpandLessIcon sx={{ fontSize: 14 }} />}>
                    {startIdx} more with higher price (lender credits)
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {visibleRates.map((rp, i) => {
              const origIdx = sorted.indexOf(rp);
              const isPar = origIdx === parIdx;
              const pi = isIO ? calcMonthlyIO(rp.rate, loanAmount) : calcMonthlyPI(rp.rate, loanAmount, p.term);
              const isRateSelected = selectedRate?.productShortName === p.productShortName && selectedRate?.rate === rp.rate;
              return (
                <TableRow
                  key={i}
                  hover
                  selected={isRateSelected}
                  onClick={() => onSelectRate?.({
                    quoteId: rp.quoteId,
                    productId: p.productId,
                    productShortName: p.productShortName,
                    productName: p.productName,
                    family: p.family,
                    subfamily: p.subfamily,
                    typeName: p.typeName,
                    paymentType: p.paymentType,
                    rate: rp.rate,
                    basePrice: rp.basePrice,
                    adjustedPrice: rp.adjustedPrice,
                    monthlyPayment: pi,
                    isIO,
                    term: p.term,
                    adjustments: p.adjustments,
                    totalAdjustment: p.totalAdjustment,
                  })}
                  sx={(theme) => ({
                    cursor: 'pointer',
                    ...(isPar && !isRateSelected ? { bgcolor: `rgba(${theme.vars.palette.warning.mainChannel} / 0.10)` } : {}),
                  })}
                >
                  <TableCell sx={{ fontWeight: 700 }}>{fmtPct(rp.rate)}</TableCell>
                  <TableCell align="right">{fmt(rp.basePrice, 3)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: rp.adjustedPrice >= 100 ? 'success.main' : 'error.main' }}>
                    {fmt(rp.adjustedPrice, 3)}
                  </TableCell>
                  <TableCell align="right">{pi ? fmtDollar(pi) : '—'}</TableCell>
                  <TableCell align="center" sx={{ width: 52 }}>
                    {isPar && <NearParBadge />}
                  </TableCell>
                </TableRow>
              );
            })}
            {!showAllRates && endIdx < sorted.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 0.5 }}>
                  <Button size="small" onClick={onToggleShowAll} sx={{ fontSize: '0.7rem', textTransform: 'none' }} startIcon={<ExpandMoreIcon sx={{ fontSize: 14 }} />}>
                    {sorted.length - endIdx} more with lower price (discount points)
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {showAllRates && hiddenCount > 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 0.5 }}>
                  <Button size="small" onClick={onToggleShowAll} sx={{ fontSize: '0.7rem', textTransform: 'none' }} startIcon={<UnfoldLessIcon sx={{ fontSize: 14 }} />}>
                    Show fewer rates
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default RateGrid;
